import { resolveEventDate, resolveRelativeDay, resolveYearOnly, resolveMonthAlternatives, overlapsDateFilter, type SemanticDate } from '../../client-db/src/event-date'
import { normalizeDateClarification, resolveContextDate, missingYearClarification, schoolYearClarification, decadeClarification, type DateClarification } from '../../client-db/src/event-date-clarification'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  accountsTable,
  eventPeopleTable,
  eventsTable,
  eventTagsTable,
  locationsTable,
  peopleTable,
  personAccountLinksTable,
  scopesTable,
  tagsTable,
} from '../../client-db/src/db/schema.ts'
import { localDB } from "@/migrate.ts";
import { asc, desc, eq, ilike, inArray, or, sql, type InferSelectModel } from 'drizzle-orm';
import { useAuthClient } from '@/stores/auth-store.ts'
import { isDemoModeEnabled } from '@/demo/demo-mode.ts'
import { parseTripTemporalAnchor } from '@/utils/trip-temporal-anchor.ts'

export type DatabaseEvent = InferSelectModel<typeof eventsTable>;

type EditableEvent = DatabaseEvent & {
  locations?: string[],
  people?: string[],
  tags?: string[],
  unmatchedPeople?: string[],
  unmatchedTags?: string[],
}

type MatchedEntity = {
  id: number
  name: string
  source: string
}

type ExtractedEventResponse = {
  title: string
  edited: string
  people: string[]
  tags: string[]
  locations: string[]
  matchedPeople: MatchedEntity[]
  matchedTags: MatchedEntity[]
  matchedLocation: MatchedEntity | null
  unmatchedPeople: string[]
  unmatchedTags: string[]
  description: string
  date?: string | null
  temporal?: SemanticDate
  dateClarification?: DateClarification | null
}

type SearchIntentResponse = {
  interpretedQuery: string
  personIds: number[]
  tagIds: number[]
  locationIds: number[]
  tagGroups: SearchTagGroup[]
  beginDate: string | null
  endDate: string | null
  residualQuery: string | null
  confidence: number
  clarificationQuestion: string | null
}

type OnboardingState = {
  profile?: {
    birthday?: string | null
    birthdayDate?: string | null
  }
}

function parseMMDDYYYY(value: string | null | undefined): Date | null {
  if (!value) return null
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!match) return null

  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3])
  if (!month || !day || !year) return null

  const parsed = new Date(year, month - 1, day)
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null
  }

  return parsed
}

function formatAsMMDDYYYYFromISO(value: string | null | undefined): string | null {
  if (!value) return null
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  return `${match[2]}/${match[3]}/${match[1]}`
}

function formatAsMMDDYYYYFromDateValue(value: Date | string | null | undefined): string | null {
  if (!value) return null
  const dateValue = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(dateValue.getTime())) return null

  return `${String(dateValue.getMonth() + 1).padStart(2, '0')}/${String(dateValue.getDate()).padStart(2, '0')}/${dateValue.getFullYear()}`
}

function formatAsISODateFromDateValue(value: Date | string | null | undefined): string | null {
  if (!value) return null
  const dateValue = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(dateValue.getTime())) return null

  return `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}-${String(dateValue.getDate()).padStart(2, '0')}`
}

function stemSearchToken(token: string) {
  const value = token.trim().toLowerCase()
  if (value.length <= 3) return value

  if (value.endsWith('ies') && value.length > 4) return `${value.slice(0, -3)}y`
  if (value.endsWith('ing') && value.length > 5) return value.slice(0, -3)
  if (value.endsWith('ed') && value.length > 4) return value.slice(0, -2)
  if (value.endsWith('s') && value.length > 3) return value.slice(0, -1)

  return value
}

function tokenizeWords(value: string) {
  return value
    .trim()
    .toLowerCase()
    .split(/[\s,./\-_'":;!?()[\]{}]+/)
    .filter(Boolean)
}

function eventTextMatchesQuery(
  event: Pick<DatabaseEvent, 'title' | 'description' | 'text'>,
  rawQuery: string,
): boolean {
  const normalizedQuery = rawQuery.trim().toLowerCase()
  if (!normalizedQuery) return true

  const eventText = `${event.title ?? ''} ${event.description ?? ''} ${event.text ?? ''}`.trim()
  if (!eventText) return false

  const eventWords = tokenizeWords(eventText)
  if (eventWords.length === 0) return false

  const rawTokens = tokenizeWords(normalizedQuery)
  if (rawTokens.length === 0) return false

  const stemmedTokens = rawTokens.map((token) => stemSearchToken(token))

  return stemmedTokens.every((token) =>
    eventWords.some((word) => {
      const stemmedWord = stemSearchToken(word)
      return word.startsWith(token) || stemmedWord.startsWith(token)
    }),
  )
}

const BROAD_INTENT_TERMS = [
  'milestone',
  'milestones',
  'family',
  'vacation',
  'vacations',
  'trip',
  'trips',
  'travel',
  'memories',
  'events',
]

const SPECIFIC_INTENT_TERMS = [
  'graduation',
  'graduated',
  'born',
  'birth',
  'birthday',
  'wedding',
  'anniversary',
  'promotion',
  'marathon',
  'school',
  'achievement',
]

type QuickEventInput = {
  title?: string | null
  description?: string | null
  text?: string | null
  date?: Date | null
  scopeId?: number | null
}

type SearchTagGroup = 'milestone'

export type SearchFilters = {
  personIds: number[]
  tagIds: number[]
  locationIds: number[]
  tagGroups: SearchTagGroup[]
  beginDate: string | null
  endDate: string | null
}

// Unified undo entry that can handle any type of change
interface UndoEntry {
  type: 'event' | 'text';
  data: EditableEvent | string; // Full event state or just text content
  timestamp: number;
}

const { post } = useAuthClient()

export const useEventStore = defineStore('event', () => {
  // State as refs
  const showEvent = ref(false)
  const currentEvent = ref<EditableEvent | undefined>(undefined)
  const originalEvent = ref<EditableEvent | undefined>(undefined)
  const editEvent = ref(false)
  const loadingEvent = ref(false)
  const captureFailure = ref<{ eventId: number; message: string } | null>(null)
  const captureProcessingError = computed(() => captureFailure.value?.eventId === currentEvent.value?.id ? captureFailure.value?.message ?? null : null)
  const isDirty = ref(false)
  const unifiedUndoStack = ref<UndoEntry[]>([])
  const unifiedRedoStack = ref<UndoEntry[]>([])

  // Search state
  const searchQuery = ref('')
  const searchResults = ref<DatabaseEvent[]>([])
  const isSearching = ref(false)
  let fullTextSearchSupported: boolean | null = null
  const searchFilters = ref<SearchFilters>({
    personIds: [],
    tagIds: [],
    locationIds: [],
    tagGroups: [],
    beginDate: null,
    endDate: null,
  })

  // Getters as computed
  const canUndo = computed(() => unifiedUndoStack.value.length > 0)
  const canRedo = computed(() => unifiedRedoStack.value.length > 0)
  const hasActiveSearch = computed(() => {
    return Boolean(searchQuery.value.trim()) ||
      searchFilters.value.personIds.length > 0 ||
      searchFilters.value.tagIds.length > 0 ||
      searchFilters.value.locationIds.length > 0 ||
      searchFilters.value.tagGroups.length > 0 ||
      Boolean(searchFilters.value.beginDate) ||
      Boolean(searchFilters.value.endDate)
  })
  const hasZeroSearchResults = computed(() => hasActiveSearch.value && searchResults.value.length === 0)
  const searchFilterSignature = computed(() => JSON.stringify(searchFilters.value))

  const milestoneTagNames = new Set([
    'milestone',
    'birthday',
    'graduation',
    'wedding',
    'anniversary',
    'engagement',
    'birth',
    'born',
    'retirement',
  ])

  function normalizeName(value: string) {
    return value.trim().toLowerCase()
  }

  async function ensureDefaultPersonalScopeId(): Promise<number | null> {
    const [existingScope] = await localDB
      .select({ id: scopesTable.id })
      .from(scopesTable)
      .where(eq(scopesTable.type, 'PERSONAL'))
      .limit(1)

    if (existingScope?.id) {
      return existingScope.id
    }

    const [createdScope] = await localDB
      .insert(scopesTable)
      .values({
        type: 'PERSONAL',
        name: 'My Timeline',
      })
      .returning({ id: scopesTable.id })

    return createdScope?.id ?? null
  }

  async function resolveEventScopeId(scopeId?: number | null): Promise<number | null> {
    if (typeof scopeId === 'number' && Number.isInteger(scopeId) && scopeId > 0) {
      return scopeId
    }

    return ensureDefaultPersonalScopeId()
  }

  async function ensureLocationIdFromEvent(locationName: string | null | undefined): Promise<number | null> {
    const trimmedName = locationName?.trim()
    if (!trimmedName) return null

    const normalizedName = normalizeName(trimmedName)

    const [existingLocation] = await localDB
      .select({ id: locationsTable.id, name: locationsTable.name })
      .from(locationsTable)
      .where(eq(locationsTable.normalizedName, normalizedName))
      .limit(1)

    if (existingLocation?.id) {
      if (currentEvent.value) {
        currentEvent.value.locations = [existingLocation.name || trimmedName]
      }
      return existingLocation.id
    }

    const [createdLocation] = await localDB
      .insert(locationsTable)
      .values({
        name: trimmedName,
        normalizedName,
      })
      .returning({ id: locationsTable.id, name: locationsTable.name })

    if (currentEvent.value && createdLocation?.name) {
      currentEvent.value.locations = [createdLocation.name]
    }

    return createdLocation?.id ?? null
  }

  async function ensurePeopleIds(names: string[], scopeId: number | null): Promise<number[]> {
    const uniqueNames = Array.from(
      new Map(
        names
          .map((name) => name.trim())
          .filter(Boolean)
          .map((name) => [normalizeName(name), name] as const),
      ).values(),
    )

    if (uniqueNames.length === 0) return []

    const existingPeople = await localDB
      .select({
        id: peopleTable.id,
        fullName: peopleTable.fullName,
      })
      .from(peopleTable)
      .where(inArray(peopleTable.fullName, uniqueNames))

    const idByNormalizedName = new Map<string, number>()
    for (const person of existingPeople) {
      if (!person.fullName) continue
      idByNormalizedName.set(normalizeName(person.fullName), person.id)
    }

    const missingNames = uniqueNames.filter((name) => !idByNormalizedName.has(normalizeName(name)))

    for (const fullName of missingNames) {
      const parts = fullName.split(/\s+/).filter(Boolean)
      const firstName = parts[0] ?? null
      const lastName = parts.length > 1 ? parts.slice(1).join(' ') : null

      const [createdPerson] = await localDB
        .insert(peopleTable)
        .values({
          scopeId: scopeId ?? null,
          firstName,
          lastName,
          fullName,
        })
        .returning({ id: peopleTable.id, fullName: peopleTable.fullName })

      if (createdPerson?.id && createdPerson.fullName) {
        idByNormalizedName.set(normalizeName(createdPerson.fullName), createdPerson.id)
      }
    }

    return uniqueNames
      .map((name) => idByNormalizedName.get(normalizeName(name)))
      .filter((id): id is number => Boolean(id))
  }

  async function getSelfPerson() {
    const [selfPerson] = await localDB
      .select({
        id: peopleTable.id,
        scopeId: peopleTable.scopeId,
        fullName: peopleTable.fullName,
        firstName: peopleTable.firstName,
        lastName: peopleTable.lastName,
        birthDate: peopleTable.birthDate,
      })
      .from(personAccountLinksTable)
      .innerJoin(peopleTable, eq(personAccountLinksTable.personId, peopleTable.id))
      .where(eq(personAccountLinksTable.status, 'SELF'))
      .limit(1)

    return selfPerson ?? null
  }

  async function updateOrCreateSelfPersonFromOnboarding(
    fullName: string,
    birthDate: Date,
    scopeId: number | null,
  ): Promise<void> {
    const trimmedName = fullName.trim()
    const parts = trimmedName.split(/\s+/).filter(Boolean)
    const firstName = parts[0] ?? null
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : null

    const selfPerson = await getSelfPerson()
    if (selfPerson?.id) {
      await localDB
        .update(peopleTable)
        .set({
          scopeId: selfPerson.scopeId ?? scopeId ?? null,
          firstName: trimmedName ? firstName : selfPerson.firstName ?? null,
          lastName: trimmedName ? lastName : selfPerson.lastName ?? null,
          fullName: trimmedName || selfPerson.fullName || null,
          birthDate,
        })
        .where(eq(peopleTable.id, selfPerson.id))
      return
    }

    const [createdPerson] = await localDB
      .insert(peopleTable)
      .values({
        scopeId,
        firstName,
        lastName,
        fullName: trimmedName || null,
        birthDate,
      })
      .returning({ id: peopleTable.id })

    if (!createdPerson?.id) return

    const [account] = await localDB
      .select({ id: accountsTable.id })
      .from(accountsTable)
      .orderBy(asc(accountsTable.id))
      .limit(1)

    if (!account?.id) return

    await localDB.insert(personAccountLinksTable).values({
      personId: createdPerson.id,
      accountId: account.id,
      status: 'SELF',
      source: 'LOCAL',
      isPrimary: true,
    })
  }

  async function ensureTagIds(tagNames: string[]): Promise<number[]> {
    const uniqueTagNames = Array.from(
      new Map(
        tagNames
          .map((tagName) => tagName.trim())
          .filter(Boolean)
          .map((tagName) => [normalizeName(tagName), tagName] as const),
      ).values(),
    )

    if (uniqueTagNames.length === 0) return []

    const normalizedTagNames = uniqueTagNames.map((tagName) => normalizeName(tagName))
    const existingTags = await localDB
      .select({ id: tagsTable.id, normalizedName: tagsTable.normalizedName })
      .from(tagsTable)
      .where(inArray(tagsTable.normalizedName, normalizedTagNames))

    const idByNormalizedName = new Map<string, number>()
    for (const tag of existingTags) {
      if (!tag.normalizedName) continue
      idByNormalizedName.set(tag.normalizedName, tag.id)
    }

    const missingTagNames = uniqueTagNames.filter(
      (tagName) => !idByNormalizedName.has(normalizeName(tagName)),
    )

    for (const name of missingTagNames) {
      const normalizedName = normalizeName(name)
      const [createdTag] = await localDB
        .insert(tagsTable)
        .values({
          name,
          normalizedName,
        })
        .returning({ id: tagsTable.id, normalizedName: tagsTable.normalizedName })

      if (createdTag?.id && createdTag.normalizedName) {
        idByNormalizedName.set(createdTag.normalizedName, createdTag.id)
      }
    }

    return normalizedTagNames
      .map((normalizedTagName) => idByNormalizedName.get(normalizedTagName))
      .filter((id): id is number => Boolean(id))
  }

  // Actions as functions
  function startEditing() {
    if (currentEvent.value) {
      originalEvent.value = JSON.parse(JSON.stringify(currentEvent.value));
      unifiedUndoStack.value = [];
      unifiedRedoStack.value = [];
      isDirty.value = false;
    }
  }

  function pushToUndoStack(type: 'event' | 'text', data: EditableEvent | string) {
    const entry: UndoEntry = {
      type,
      data: type === 'event' ? JSON.parse(JSON.stringify(data)) : data as string,
      timestamp: Date.now()
    };

    unifiedUndoStack.value.push(entry);
    unifiedRedoStack.value = []; // Clear redo stack when new change is made
    isDirty.value = true;
  }

  function updateEventInMemory<K extends keyof EditableEvent>(field: K, value: EditableEvent[K]) {
    if (!currentEvent.value) return;

    // Push current state to undo stack before making changes
    pushToUndoStack('event', currentEvent.value);

    (currentEvent.value as any)[field] = value;
    isDirty.value = true;
  }

  function updateTextWithUndo(newText: string, previousText: string) {
    if (!currentEvent.value) return;

    // Push the previous text state to undo stack
    pushToUndoStack('text', previousText);

    // Update the current text
    currentEvent.value.text = newText;
    isDirty.value = originalEvent.value ?
      JSON.stringify(currentEvent.value) !== JSON.stringify(originalEvent.value) :
      true;
  }

  function updateTextOnly(newText: string) {
    if (!currentEvent.value) return;

    currentEvent.value.text = newText;
    isDirty.value = originalEvent.value ?
      JSON.stringify(currentEvent.value) !== JSON.stringify(originalEvent.value) :
      true;
  }

  function undo(): { type: 'event' | 'text' | 'milkdown' | null, data?: any } {
    if (unifiedUndoStack.value.length === 0 || !currentEvent.value) {
      return { type: null };
    }

    const currentEntry: UndoEntry = {
      type: 'event',
      data: JSON.parse(JSON.stringify(currentEvent.value)),
      timestamp: Date.now()
    };

    // Push current state to redo stack
    unifiedRedoStack.value.push(currentEntry);

    // Get the previous state
    const previousEntry = unifiedUndoStack.value.pop()!;

    if (previousEntry.type === 'event') {
      // Restore full event state
      currentEvent.value = JSON.parse(JSON.stringify(previousEntry.data as EditableEvent));

      // Update dirty state
      isDirty.value = originalEvent.value ?
        JSON.stringify(currentEvent.value) !== JSON.stringify(originalEvent.value) :
        true;

      return { type: 'event', data: currentEvent.value };
    } else {
      // Restore text state
      const previousText = previousEntry.data as string;
      currentEvent.value.text = previousText;

      isDirty.value = originalEvent.value ?
        JSON.stringify(currentEvent.value) !== JSON.stringify(originalEvent.value) :
        true;

      return { type: 'text', data: previousText };
    }
  }

  function redo(): { type: 'event' | 'text' | null, data?: any } {
    if (unifiedRedoStack.value.length === 0 || !currentEvent.value) {
      return { type: null };
    }

    const currentEntry: UndoEntry = {
      type: 'event',
      data: JSON.parse(JSON.stringify(currentEvent.value)),
      timestamp: Date.now()
    };

    // Push current state to undo stack
    unifiedUndoStack.value.push(currentEntry);

    // Get the next state
    const nextEntry = unifiedRedoStack.value.pop()!;

    if (nextEntry.type === 'event') {
      // Restore full event state
      currentEvent.value = JSON.parse(JSON.stringify(nextEntry.data as EditableEvent));

      isDirty.value = originalEvent.value ?
        JSON.stringify(currentEvent.value) !== JSON.stringify(originalEvent.value) :
        true;

      return { type: 'event', data: currentEvent.value };
    } else {
      // Restore text state
      const nextText = nextEntry.data as string;
      currentEvent.value.text = nextText;

      isDirty.value = originalEvent.value ?
        JSON.stringify(currentEvent.value) !== JSON.stringify(originalEvent.value) :
        true;

      return { type: 'text', data: nextText };
    }
  }

  function syncSavedFields(next: Partial<DatabaseEvent>, eventId: number) {
    if (currentEvent.value?.id !== eventId) return
    Object.assign(currentEvent.value, next)
    if (originalEvent.value) Object.assign(originalEvent.value, JSON.parse(JSON.stringify(next)))
    isDirty.value = JSON.stringify(currentEvent.value) !== JSON.stringify(originalEvent.value)
    for (const entry of [...unifiedUndoStack.value, ...unifiedRedoStack.value]) {
      if (entry.type === 'event') Object.assign(entry.data, JSON.parse(JSON.stringify(next)))
    }
  }

  async function saveEventDate(next: Pick<DatabaseEvent, 'date' | 'endDate' | 'datePrecision'>): Promise<boolean> {
    if (!currentEvent.value?.id) return false
    const eventId = currentEvent.value.id
    const clarification = currentEvent.value.dateClarification
    const fields = {
      ...next,
      ...(clarification?.status === 'pending' ? {
        dateClarification: { ...clarification, status: next.date && !['unknown', 'range'].includes(next.datePrecision ?? '') ? 'answered' as const : 'dismissed' as const },
      } : {}),
    }
    try {
      await localDB.update(eventsTable).set(fields).where(eq(eventsTable.id, eventId))
      // Keep date and clarification changes out of unrelated text undo/discard operations.
      syncSavedFields(fields, eventId)
      return true
    } catch (error) {
      console.error('Failed to save event date:', error)
      return false
    }
  }

  async function dismissDateClarification(): Promise<boolean> {
    if (!currentEvent.value?.id || !currentEvent.value.dateClarification) return false
    const eventId = currentEvent.value.id
    const fields = { dateClarification: { ...currentEvent.value.dateClarification, status: 'dismissed' as const } }
    try {
      await localDB.update(eventsTable).set(fields).where(eq(eventsTable.id, eventId))
      syncSavedFields(fields, eventId)
      return true
    } catch (error) {
      console.error('Failed to dismiss date clarification:', error)
      return false
    }
  }

  async function saveChanges(): Promise<boolean> {
    if (!currentEvent.value || !currentEvent.value.id || !isDirty.value) return true;

    try {
      const resolvedScopeId = await resolveEventScopeId(currentEvent.value.scopeId ?? null)
      const primaryLocationName = currentEvent.value.locations?.[0] ?? null
      const locationId = await ensureLocationIdFromEvent(primaryLocationName)

      await localDB
        .update(eventsTable)
        .set({
          scopeId: resolvedScopeId,
          title: currentEvent.value.title,
          description: currentEvent.value.description,
          text: currentEvent.value.text,
          date: currentEvent.value.date ? new Date(currentEvent.value.date) : null,
          endDate: currentEvent.value.endDate ? new Date(currentEvent.value.endDate) : null,
          datePrecision: currentEvent.value.datePrecision ?? null,
          parentEventId: currentEvent.value.parentEventId ?? null,
          afterEventEndId: currentEvent.value.afterEventEndId ?? null,
          locationId,
        })
        .where(eq(eventsTable.id, currentEvent.value.id));

      currentEvent.value.scopeId = resolvedScopeId
      currentEvent.value.locationId = locationId

      const personIds = await ensurePeopleIds(
        currentEvent.value.people ?? [],
        resolvedScopeId,
      )

      await localDB.delete(eventPeopleTable).where(eq(eventPeopleTable.eventId, currentEvent.value.id))
      if (personIds.length > 0) {
        await localDB.insert(eventPeopleTable).values(
          personIds.map((personId) => ({
            eventId: currentEvent.value!.id,
            personId,
            relevance: 1,
          })),
        )
      }

      const tagIds = await ensureTagIds(currentEvent.value.tags ?? [])
      await localDB.delete(eventTagsTable).where(eq(eventTagsTable.eventId, currentEvent.value.id))
      if (tagIds.length > 0) {
        await localDB.insert(eventTagsTable).values(
          tagIds.map((tagId) => ({
            eventId: currentEvent.value!.id,
            tagId,
          })),
        )
      }

      // Update original state and clear dirty flag
      originalEvent.value = JSON.parse(JSON.stringify(currentEvent.value));
      isDirty.value = false;
      unifiedUndoStack.value = [];
      unifiedRedoStack.value = [];
      return true;
    } catch (error) {
      console.error('Failed to save changes:', error);
      return false;
    }
  }

  function discardChanges() {
    if (originalEvent.value) {
      currentEvent.value = JSON.parse(JSON.stringify(originalEvent.value));
      isDirty.value = false;
      unifiedUndoStack.value = [];
      unifiedRedoStack.value = [];
    }
  }

  function loadExistingEvent(event: EditableEvent) {
    captureFailure.value = null
    currentEvent.value = JSON.parse(JSON.stringify(event));
    originalEvent.value = JSON.parse(JSON.stringify(event));
    showEvent.value = true;
    isDirty.value = false;
    unifiedUndoStack.value = [];
    unifiedRedoStack.value = [];
  }

  async function createEventFromText(message: string): Promise<EditableEvent> {
    loadingEvent.value = true;
    showEvent.value = true;
    captureFailure.value = null
    let extracted: (ExtractedEventResponse & { dbEvent: DatabaseEvent }) | undefined
    let dbEvent: DatabaseEvent
    try {
      const scopeId = await resolveEventScopeId()
      const [saved] = await localDB.insert(eventsTable).values({
        scopeId, originalText: message, text: message, title: message.slice(0, 80),
        date: null, endDate: null, datePrecision: 'unknown',
      }).returning()
      dbEvent = saved
      try {
        extracted = await extractInfo(message, saved.id)
        dbEvent = extracted.dbEvent
      } catch (error) {
        console.warn('Memory saved; extraction failed:', error)
        const [persisted] = await localDB.select().from(eventsTable).where(eq(eventsTable.id, saved.id))
        dbEvent = persisted ?? saved
        captureFailure.value = { eventId: saved.id, message: 'Memory saved. Dot could not process it. You can edit the date yourself.' }
      }
    } finally {
      loadingEvent.value = false
    }

    const nextEvent: EditableEvent = {
      ...dbEvent,
      locations: extracted?.matchedLocation?.name
        ? [extracted.matchedLocation.name]
        : extracted?.locations,
      people: extracted?.matchedPeople?.map((person: MatchedEntity) => person.name) ?? [],
      tags: extracted?.matchedTags?.map((tag: MatchedEntity) => tag.name) ?? [],
      unmatchedPeople: extracted?.unmatchedPeople ?? [],
      unmatchedTags: extracted?.unmatchedTags ?? [],
    };
    currentEvent.value = nextEvent;
    loadingEvent.value = false;

    // Start editing session
    startEditing();

    return nextEvent;
  }

  async function createOnboardingEvent(profile: {
    name: string;
    birthdayDate: string;
    birthdayTime?: string | null;
  }): Promise<DatabaseEvent | null> {
    const trimmedName = profile.name.trim();
    const trimmedDate = profile.birthdayDate.trim();

    if (!trimmedDate) {
      return null;
    }

    const birthDateTime = profile.birthdayTime?.trim()
      ? new Date(`${trimmedDate}T${profile.birthdayTime.trim()}`)
      : new Date(`${trimmedDate}T00:00`);

    if (Number.isNaN(birthDateTime.getTime())) {
      return null;
    }

    const formattedBirthday = `${String(birthDateTime.getMonth() + 1).padStart(2, '0')}/${String(birthDateTime.getDate()).padStart(2, '0')}/${birthDateTime.getFullYear()}`;

    const textParts = [
      `Name: ${trimmedName || 'Unknown'}`,
      `Birthday: ${formattedBirthday}`,
      profile.birthdayTime?.trim() ? `Birth time: ${profile.birthdayTime.trim()}` : null,
    ].filter(Boolean);

    try {
      const scopeId = await resolveEventScopeId()
      await updateOrCreateSelfPersonFromOnboarding(trimmedName, birthDateTime, scopeId)
      const [insertedEvent] = await localDB
        .insert(eventsTable)
        .values({
          scopeId,
          title: 'I was born',
          description: trimmedName ? `I, ${trimmedName}, was born.` : 'Birth details',
          text: textParts.join('\n'),
          date: birthDateTime,
        })
        .returning();

      return insertedEvent ?? null;
    } catch (error) {
      console.error('Failed to create onboarding event:', error);
      return null;
    }
  }

  async function createQuickEvent(input?: QuickEventInput): Promise<EditableEvent | null> {
    try {
      const scopeId = await resolveEventScopeId(input?.scopeId ?? null)
      const [insertedEvent] = await localDB
        .insert(eventsTable)
        .values({
          scopeId,
          title: input?.title ?? null,
          description: input?.description ?? null,
          text: input?.text ?? null,
          date: input?.date ?? null,
          datePrecision: input?.date ? null : 'unknown',
        })
        .returning()

      if (!insertedEvent) {
        return null
      }

      const nextEvent: EditableEvent = {
        ...insertedEvent,
      }
      currentEvent.value = nextEvent
      originalEvent.value = JSON.parse(JSON.stringify(currentEvent.value))
      showEvent.value = true
      loadingEvent.value = false
      unifiedUndoStack.value = []
      unifiedRedoStack.value = []
      isDirty.value = false

      return nextEvent
    } catch (error) {
      console.error('Failed to create quick event:', error)
      return null
    }
  }

  async function deleteEvent(eventId: number | null): Promise<boolean> {
    if (!eventId) return false;
    try {
      await localDB.delete(eventsTable).where(eq(eventsTable.id, eventId));
      console.log('Event deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting event:', error);
      return false;
    }
  }

  async function extractInfo(message: string, savedEventId: number): Promise<ExtractedEventResponse & { id: number; dbEvent: DatabaseEvent }> {
    const tripAnchor = parseTripTemporalAnchor(message)
    const [people, tags, locations, selfPeople] = await Promise.all([
      localDB.select({ id: peopleTable.id, name: peopleTable.fullName }).from(peopleTable),
      localDB.select({ id: tagsTable.id, name: tagsTable.name }).from(tagsTable),
      localDB.select({ id: locationsTable.id, name: locationsTable.name }).from(locationsTable),
      localDB
        .select({
          id: peopleTable.id,
          name: peopleTable.fullName,
          birthDate: peopleTable.birthDate,
        })
        .from(personAccountLinksTable)
        .innerJoin(peopleTable, eq(personAccountLinksTable.personId, peopleTable.id))
        .where(eq(personAccountLinksTable.status, 'SELF'))
        .limit(1),
    ])

    const existingPeople = people
      .filter((person) => Boolean(person.name?.trim()))
      .map((person) => ({ id: person.id, name: person.name!.trim() }))

    const existingTags = tags
      .filter((tag) => Boolean(tag.name?.trim()))
      .map((tag) => ({ id: tag.id, name: tag.name!.trim() }))

    const existingLocations = locations
      .filter((location) => Boolean(location.name?.trim()))
      .map((location) => ({ id: location.id, name: location.name!.trim() }))

    const authorSelfPerson =
      selfPeople
        .map((person) => ({
          id: person.id,
          name: person.name?.trim() ?? '',
          birthDate: person.birthDate,
        }))
        .find((person) => Boolean(person.name)) ?? null

    const authorPerson = authorSelfPerson
      ? {
          id: authorSelfPerson.id,
          name: authorSelfPerson.name,
        }
      : null

    let authorBirthDate = formatAsMMDDYYYYFromDateValue(authorSelfPerson?.birthDate)
    if (!authorBirthDate) {
      try {
        const rawOnboardingState = localStorage.getItem('timethread-onboarding-v1')
        if (rawOnboardingState) {
          const onboardingState = JSON.parse(rawOnboardingState) as OnboardingState
          authorBirthDate = formatAsMMDDYYYYFromISO(
            onboardingState.profile?.birthdayDate ?? onboardingState.profile?.birthday ?? null,
          )
        }
      } catch {
        authorBirthDate = null
      }
    }

    const referenceDate = formatAsISODateFromDateValue(new Date())!
    const schoolDate = schoolYearClarification(message) ?? decadeClarification(message)
    const relativeDay = tripAnchor ? null : resolveRelativeDay(message, referenceDate)
    const yearOnly = tripAnchor ? null : resolveYearOnly(message)
    const monthAlternatives = tripAnchor ? null : resolveMonthAlternatives(message)
    const groundedDate = schoolDate ? null : relativeDay ?? monthAlternatives ?? yearOnly
    const ageDate = tripAnchor ? null : resolveContextDate(message, authorBirthDate, referenceDate)
    if (groundedDate || ageDate) {
      await localDB.update(eventsTable).set({
        ...resolveEventDate(groundedDate ?? ageDate!.temporal), dateClarification: groundedDate ? normalizeDateClarification(null, message, groundedDate.precision, referenceDate) : ageDate!.clarification,
      }).where(eq(eventsTable.id, savedEventId))
    } else {
      const partialDate = missingYearClarification(message, referenceDate) ?? schoolDate
      if (partialDate) await localDB.update(eventsTable).set({ dateClarification: partialDate }).where(eq(eventsTable.id, savedEventId))
    }
    if (!navigator.onLine) throw new Error('Offline')
    const response = await post('/api/generate-event', {
      message: tripAnchor?.activity ?? message,
      existingPeople,
      existingTags,
      existingLocations,
      authorPerson,
      authorBirthDate,
      isDemoMode: isDemoModeEnabled(),
      referenceDate,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    if (!response.ok) {
      throw new Error(`Extraction failed: ${response.status}`)
    }
    const extractedEvent = (await response.json()) as ExtractedEventResponse

    const scopeId = await resolveEventScopeId()
    const extractedDates = schoolDate ? resolveEventDate({ precision: 'unknown' }) : groundedDate ? resolveEventDate(groundedDate) : tripAnchor || extractedEvent.temporal
      ? resolveEventDate(tripAnchor ? { precision: 'unknown' } : extractedEvent.temporal)
      : { date: parseMMDDYYYY(extractedEvent.date), endDate: null, datePrecision: parseMMDDYYYY(extractedEvent.date) ? null : ('unknown' as const) }
    const useAgeDate = !groundedDate && ageDate && (['unknown', 'range'].includes(extractedDates.datePrecision ?? '') || ageDate.clarification?.kind === 'year_range')

    const event: typeof eventsTable.$inferInsert = {
      scopeId,
      title: extractedEvent.title,
      description: extractedEvent.description,
      text: extractedEvent.edited,
      ...(useAgeDate ? resolveEventDate(ageDate.temporal) : extractedDates),
      originalText: message,
    }

    event.dateClarification = useAgeDate ? ageDate.clarification : normalizeDateClarification(
      extractedEvent.dateClarification,
      message,
      event.datePrecision,
      referenceDate,
    )
    const insertedEvent = await localDB.transaction(async (db) => {
      const [updated] = await db
        .update(eventsTable)
        .set(event)
        .where(eq(eventsTable.id, savedEventId))
        .returning()
      if (!updated) throw new Error('Saved memory no longer exists')
      const insertedEvent = updated

      const eventId = insertedEvent?.id
      if (eventId) {
        const matchedLocationId = extractedEvent.matchedLocation?.id ?? null
        if (matchedLocationId) {
          await db
            .update(eventsTable)
            .set({ locationId: matchedLocationId })
            .where(eq(eventsTable.id, eventId))
        }

        const matchedPersonIds = [
          ...new Set(extractedEvent.matchedPeople.map((person) => person.id)),
        ].filter((personId): personId is number => Number.isInteger(personId) && personId > 0)
        if (matchedPersonIds.length > 0) {
          await db.insert(eventPeopleTable).values(
            matchedPersonIds.map((personId) => ({
              eventId,
              personId,
              relevance: 1,
            })),
          )
        }

        const matchedTagIds = [...new Set(extractedEvent.matchedTags.map((tag) => tag.id))].filter(
          (tagId): tagId is number => Number.isInteger(tagId) && tagId > 0,
        )
        if (matchedTagIds.length > 0) {
          await db.insert(eventTagsTable).values(
            matchedTagIds.map((tagId) => ({
              eventId,
              tagId,
            })),
          )
        }
      }

      return insertedEvent
    })

    return {
      ...extractedEvent,
      id: insertedEvent.id,
      dbEvent: insertedEvent,
    }
  }

  async function parseSearchIntentWithAi(rawQuery: string): Promise<DatabaseEvent[]> {
    const query = rawQuery.trim()
    if (!query) {
      return searchEvents(rawQuery)
    }

    const [people, tags, locations] = await Promise.all([
      localDB
        .select({ id: peopleTable.id, name: peopleTable.fullName, birthDate: peopleTable.birthDate })
        .from(peopleTable),
      localDB
        .select({ id: tagsTable.id, name: tagsTable.name })
        .from(tagsTable),
      localDB
        .select({ id: locationsTable.id, name: locationsTable.name })
        .from(locationsTable),
    ])

    const existingPeople = people
      .filter((person) => Boolean(person.name?.trim()))
      .map((person) => ({
        id: person.id,
        name: person.name!.trim(),
        birthDate: formatAsISODateFromDateValue(person.birthDate),
      }))

    const existingTags = tags
      .filter((tag) => Boolean(tag.name?.trim()))
      .map((tag) => ({ id: tag.id, name: tag.name!.trim() }))

    const existingLocations = locations
      .filter((location) => Boolean(location.name?.trim()))
      .map((location) => ({ id: location.id, name: location.name!.trim() }))

    const response = await post('/api/parse-search-intent', {
      query,
      existingPeople,
      existingTags,
      existingLocations,
      tagGroups: ['milestone'],
      isDemoMode: isDemoModeEnabled(),
    })

    if (!response.ok) {
      throw new Error(`AI search intent request failed (${response.status})`)
    }

    const parsed = await response.json() as SearchIntentResponse
    const validPersonIds = new Set(existingPeople.map((person) => person.id))
    const validTagIds = new Set(existingTags.map((tag) => tag.id))
    const validLocationIds = new Set(existingLocations.map((location) => location.id))
    const allowedTagGroups = new Set<SearchTagGroup>(['milestone'])

    const personIds = [...new Set(parsed.personIds)]
      .filter((id): id is number => Number.isInteger(id) && validPersonIds.has(id))
    const tagIds = [...new Set(parsed.tagIds)]
      .filter((id): id is number => Number.isInteger(id) && validTagIds.has(id))
    const locationIds = [...new Set(parsed.locationIds)]
      .filter((id): id is number => Number.isInteger(id) && validLocationIds.has(id))
    const tagGroups = [...new Set(parsed.tagGroups)]
      .filter((group): group is SearchTagGroup => allowedTagGroups.has(group))

    const normalizedQuery = query.toLowerCase()
    const hasBroadIntentTerm = BROAD_INTENT_TERMS.some((term) => new RegExp(`\\b${term}\\b`, 'i').test(normalizedQuery))
    const hasSpecificIntentTerm = SPECIFIC_INTENT_TERMS.some((term) => new RegExp(`\\b${term}\\b`, 'i').test(normalizedQuery))

    const matchedPersonNames = personIds
      .map((id) => existingPeople.find((person) => person.id === id)?.name ?? '')
      .filter(Boolean)

    const extractTopicResidual = () => {
      let candidate = query
      for (const personName of matchedPersonNames) {
        const escapedName = personName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        candidate = candidate.replace(new RegExp(`\\b${escapedName}\\b`, 'ig'), ' ')
      }

      candidate = candidate
        .replace(/\b(when|at|on|in|for|with|during|from|to|the|a|an|and|of|was|were|is|are)\b/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()

      return candidate
    }

    let beginDate = parsed.beginDate ?? null
    let endDate = parsed.endDate ?? null
    if (beginDate && endDate && beginDate > endDate) {
      const previousBegin = beginDate
      beginDate = endDate
      endDate = previousBegin
    }

    let nextTagGroups = tagGroups
    if (hasSpecificIntentTerm && !hasBroadIntentTerm && nextTagGroups.includes('milestone')) {
      nextTagGroups = nextTagGroups.filter((group) => group !== 'milestone')
    }

    let residualQuery = parsed.residualQuery?.trim() ?? ''
    if (!residualQuery && hasSpecificIntentTerm) {
      residualQuery = extractTopicResidual()
    }

    const nextSearchQuery = residualQuery || ''
    setSearchQuery(nextSearchQuery)
    searchFilters.value = {
      personIds,
      tagIds,
      locationIds,
      tagGroups: nextTagGroups,
      beginDate,
      endDate,
    }

    const hasStructuredFilters = personIds.length > 0 ||
      tagIds.length > 0 ||
      locationIds.length > 0 ||
      nextTagGroups.length > 0 ||
      Boolean(beginDate) ||
      Boolean(endDate)

    if (!hasStructuredFilters && !nextSearchQuery) {
      return searchEvents(query)
    }

    const narrowedResults = await searchEvents(nextSearchQuery)
    if (narrowedResults.length > 0) {
      return narrowedResults
    }

    // If aggressive narrowing produced no matches, fall back to the original query.
    setSearchQuery(query)
    searchFilters.value = {
      personIds: [],
      tagIds: [],
      locationIds: [],
      tagGroups: [],
      beginDate: null,
      endDate: null,
    }
    return searchEvents(query)
  }

  // Search functions
  function setSearchQuery(query: string) {
    searchQuery.value = query;
  }

  function setSearchFilters(nextFilters: Partial<SearchFilters>) {
    searchFilters.value = {
      ...searchFilters.value,
      ...nextFilters,
    }
  }

  function clearSearchFilters() {
    searchFilters.value = {
      personIds: [],
      tagIds: [],
      locationIds: [],
      tagGroups: [],
      beginDate: null,
      endDate: null,
    }
  }

  function applyAllowedIds(candidateIds: Set<number>, allowedIds: Set<number>) {
    for (const eventId of [...candidateIds]) {
      if (!allowedIds.has(eventId)) {
        candidateIds.delete(eventId)
      }
    }
  }

  async function getEventIdsByEventTextFallback(query: string): Promise<Set<number>> {
    const normalizedRawQuery = query.trim()
    const rows = await localDB
      .select({
        id: eventsTable.id,
        title: eventsTable.title,
        description: eventsTable.description,
        text: eventsTable.text,
      })
      .from(eventsTable)

    return new Set<number>(
      rows
        .filter((row) => eventTextMatchesQuery(row, normalizedRawQuery))
        .map((row) => row.id),
    )
  }

  async function getEventIdsByEventTextFilter(query: string): Promise<Set<number>> {
    const normalizedRawQuery = query.trim()
    if (!normalizedRawQuery) return new Set<number>()

    if (fullTextSearchSupported === false) {
      return getEventIdsByEventTextFallback(normalizedRawQuery)
    }

    try {
      const rows = await localDB
        .select({ id: eventsTable.id })
        .from(eventsTable)
        .where(
          sql`to_tsvector('english', concat_ws(' ', coalesce(${eventsTable.title}, ''), coalesce(${eventsTable.description}, ''), coalesce(${eventsTable.text}, '')))
              @@ websearch_to_tsquery('english', ${normalizedRawQuery})`,
        )

      fullTextSearchSupported = true
      return new Set<number>(rows.map((row) => row.id))
    } catch (error) {
      fullTextSearchSupported = false
      console.warn('Full-text search unavailable in local DB; using fallback matcher.', error)
      return getEventIdsByEventTextFallback(normalizedRawQuery)
    }
  }

  async function getEventIdsByQuery(query: string): Promise<Set<number>> {
    const startsWithTerm = `${query.trim()}%`
    const wordPrefixTerm = `% ${query.trim()}%`
    const normalizedQuery = normalizeName(query)

    const [eventIdsByText, peopleRows, tagRows, locationRows] = await Promise.all([
      getEventIdsByEventTextFilter(query),
      localDB
        .select({ eventId: eventPeopleTable.eventId })
        .from(eventPeopleTable)
        .innerJoin(peopleTable, eq(eventPeopleTable.personId, peopleTable.id))
        .where(
          or(
            ilike(peopleTable.fullName, query.trim()),
            ilike(peopleTable.fullName, startsWithTerm),
            ilike(peopleTable.fullName, wordPrefixTerm),
          ),
        ),
      localDB
        .select({ eventId: eventTagsTable.eventId })
        .from(eventTagsTable)
        .innerJoin(tagsTable, eq(eventTagsTable.tagId, tagsTable.id))
        .where(
          or(
            ilike(tagsTable.name, query.trim()),
            ilike(tagsTable.name, startsWithTerm),
            ilike(tagsTable.name, wordPrefixTerm),
            eq(tagsTable.normalizedName, normalizedQuery),
          ),
        ),
      localDB
        .select({ id: eventsTable.id })
        .from(eventsTable)
        .innerJoin(locationsTable, eq(eventsTable.locationId, locationsTable.id))
        .where(
          or(
            ilike(locationsTable.name, query.trim()),
            ilike(locationsTable.name, startsWithTerm),
            ilike(locationsTable.name, wordPrefixTerm),
            eq(locationsTable.normalizedName, normalizedQuery),
          ),
        ),
    ])

    return new Set<number>([
      ...eventIdsByText,
      ...peopleRows.map((row) => row.eventId),
      ...tagRows.map((row) => row.eventId),
      ...locationRows.map((row) => row.id),
    ])
  }

  async function getEventIdsByPersonFilter(personIds: number[]): Promise<Set<number>> {
    if (personIds.length === 0) return new Set<number>()

    const peopleRows = await localDB
      .select({ eventId: eventPeopleTable.eventId })
      .from(eventPeopleTable)
      .where(inArray(eventPeopleTable.personId, personIds))

    return new Set<number>(peopleRows.map((row) => row.eventId))
  }

  async function getEventIdsByTagFilter(tagIds: number[], tagGroups: SearchTagGroup[]): Promise<Set<number>> {
    const nextTagIds = new Set<number>(tagIds)

    if (tagGroups.includes('milestone')) {
      const milestoneRows = await localDB
        .select({ id: tagsTable.id })
        .from(tagsTable)
        .where(inArray(tagsTable.normalizedName, [...milestoneTagNames]))

      for (const row of milestoneRows) {
        nextTagIds.add(row.id)
      }
    }

    if (nextTagIds.size === 0) return new Set<number>()

    const tagRows = await localDB
      .select({ eventId: eventTagsTable.eventId })
      .from(eventTagsTable)
      .where(inArray(eventTagsTable.tagId, [...nextTagIds]))

    return new Set<number>(tagRows.map((row) => row.eventId))
  }

  async function getEventIdsByLocationFilter(locationIds: number[]): Promise<Set<number>> {
    if (locationIds.length === 0) return new Set<number>()

    const locationRows = await localDB
      .select({ id: eventsTable.id })
      .from(eventsTable)
      .where(inArray(eventsTable.locationId, locationIds))

    return new Set<number>(locationRows.map((row) => row.id))
  }

  function getEventIdsByDateRangeFilter(allEvents: DatabaseEvent[], beginDate: string | null, endDate: string | null): Set<number> {
    return new Set(allEvents.filter(event => overlapsDateFilter(event, beginDate, endDate)).map(event => event.id))
  }

  async function searchEvents(query: string = searchQuery.value): Promise<DatabaseEvent[]> {
    const normalizedQuery = query.trim()
    setSearchQuery(query)

    if (!normalizedQuery && !hasActiveSearch.value) {
      return getAllEvents()
    }

    isSearching.value = true;

    try {
      const allEvents = await localDB
        .select()
        .from(eventsTable)
        .orderBy(desc(eventsTable.date));

      const candidateIds = new Set<number>(allEvents.map((event) => event.id))

      if (normalizedQuery) {
        const queryIds = await getEventIdsByQuery(normalizedQuery)
        applyAllowedIds(candidateIds, queryIds)
      }

      if (searchFilters.value.personIds.length > 0) {
        const personIds = await getEventIdsByPersonFilter(searchFilters.value.personIds)
        applyAllowedIds(candidateIds, personIds)
      }

      if (searchFilters.value.tagIds.length > 0 || searchFilters.value.tagGroups.length > 0) {
        const tagIds = await getEventIdsByTagFilter(
          searchFilters.value.tagIds,
          searchFilters.value.tagGroups,
        )
        applyAllowedIds(candidateIds, tagIds)
      }

      if (searchFilters.value.locationIds.length > 0) {
        const locationIds = await getEventIdsByLocationFilter(searchFilters.value.locationIds)
        applyAllowedIds(candidateIds, locationIds)
      }

      if (searchFilters.value.beginDate || searchFilters.value.endDate) {
        const dateRangeIds = getEventIdsByDateRangeFilter(
          allEvents,
          searchFilters.value.beginDate,
          searchFilters.value.endDate,
        )
        applyAllowedIds(candidateIds, dateRangeIds)
      }

      const results = allEvents.filter((event) => candidateIds.has(event.id))
      searchResults.value = results;
      return results;
    } catch (error) {
      console.error('Search error:', error);
      return [];
    } finally {
      isSearching.value = false;
    }
  }

  async function getAllEvents(): Promise<DatabaseEvent[]> {
    const results = await localDB
      .select()
      .from(eventsTable)
      .orderBy(desc(eventsTable.date));

    searchResults.value = results;
    return results;
  }

  function clearSearch() {
    searchQuery.value = '';
    clearSearchFilters()
    searchResults.value = [];
  }

  // Return all state, computed properties, and functions
  return {
    // State
    showEvent,
    currentEvent,
    originalEvent,
    editEvent,
    loadingEvent,
    captureProcessingError,
    isDirty,
    unifiedUndoStack,
    unifiedRedoStack,
    searchQuery,
    searchResults,
    isSearching,
    searchFilters,
    hasActiveSearch,
    hasZeroSearchResults,
    searchFilterSignature,

    // Computed
    canUndo,
    canRedo,

    // Actions
    startEditing,
    pushToUndoStack,
    updateEventInMemory,
    updateTextWithUndo,
    updateTextOnly,
    undo,
    redo,
    saveChanges,
    saveEventDate,
    dismissDateClarification,
    discardChanges,
    loadExistingEvent,
    createEventFromText,
    createQuickEvent,
    createOnboardingEvent,
    deleteEvent,
    extractInfo,
    setSearchQuery,
    setSearchFilters,
    clearSearchFilters,
    searchEvents,
    parseSearchIntentWithAi,
    getAllEvents,
    clearSearch,
  }
})
